import type { Certificate, Experience } from '../types';
import { formatDate } from '../utils/utils';

/**
 * Experience row - same typographic system as the project spec table:
 * uppercase micro-labels, hairline rules, tabular dates.
 */
function createExperienceCard(experience: Experience): HTMLLIElement {
  const startDate = formatDate(experience.start_date);
  const endDate = formatDate(experience.end_date);
  const dateLabel = `${experience.start_date} to ${experience.end_date || 'present'}`;

  const skillsList = experience.skills
    .map(skill => `<li>${skill}</li>`)
    .join('');

  const card = document.createElement('li');
  card.className = 'spec-row';
  card.innerHTML = `
    <p class="spec-date" aria-label="${dateLabel}">${startDate} - ${endDate}</p>
    <div>
      <h3>
        <a href="${experience.link}" target="_blank" rel="noreferrer"
          aria-label="${experience.title} at ${experience.company}">
          ${experience.title} · ${experience.company}
        </a>
      </h3>
      <p class="spec-desc">${experience.description}</p>
      <ul class="spec-skills" aria-label="Technologies used">${skillsList}</ul>
    </div>
  `;
  return card;
}

function createCertificateCard(certificate: Certificate): HTMLLIElement {
  const achievedDate = formatDate(certificate.achieved_date, true);

  const card = document.createElement('li');
  card.className = 'spec-row';
  card.innerHTML = `
    <p class="spec-date" aria-label="${certificate.achieved_date}">${achievedDate}</p>
    <div>
      <h3>${certificate.title} · ${certificate.company}</h3>
    </div>
  `;
  return card;
}

/** Load and display experiences from the API. */
export async function loadExperiences(): Promise<void> {
  const container = document.getElementById('experienceContainer');
  if (!container) return;
  try {
    const response = await fetch('/api/experiences');
    const experiences: Experience[] = await response.json();

    const list = document.createElement('ol');
    experiences
      .filter(exp => exp.status === 'published')
      .sort((a, b) => ((a.sort || 999) - (b.sort || 999)))
      .forEach(exp => list.appendChild(createExperienceCard(exp)));

    container.appendChild(list);
  } catch (error) {
    console.error('Error loading experiences:', error);
    container.innerHTML = '<p class="spec-desc">Failed to load experiences. Please try again later.</p>';
  }
}

/** Load and display certificates from the API. */
export async function loadCertificates(): Promise<void> {
  const container = document.getElementById('certificateContainer');
  if (!container) return;
  try {
    const response = await fetch('/api/certificates');
    const certificates: Certificate[] = await response.json();

    const list = document.createElement('ol');
    certificates
      .filter(cert => cert.status === 'published')
      .sort((a, b) => new Date(b.achieved_date).getTime() - new Date(a.achieved_date).getTime())
      .forEach(cert => list.appendChild(createCertificateCard(cert)));

    container.appendChild(list);
  } catch (error) {
    console.error('Error loading certificates:', error);
    container.innerHTML = '<p class="spec-desc">Failed to load certificates. Please try again later.</p>';
  }
}
